import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { UpdateProfileData } from '../types/auth';
import { propertyApi } from '../services';

import './ProfilePage.css';

interface ProfileFormData {
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface FormErrors {
  email?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
  general?: string;
}

const ProfilePage: React.FC = () => {
  const { user, updateProfile, isLoading, error, clearError } = useAuth();
  const [propertyCount, setPropertyCount] = useState<number | null>(null);

  // Form state
  const [formData, setFormData] = useState<ProfileFormData>({
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        email: user.email,
      }));
    }
  }, [user]);

  // Fetch property count
  useEffect(() => {
    const fetchPropertyCount = async () => {
      try {
        const data = await propertyApi.getAll();
        setPropertyCount(data.length);
      } catch (err) {
        console.error('Failed to fetch property count for profile', err);
      }
    };
    fetchPropertyCount();
  }, []);

  // Clear messages when form data changes
  useEffect(() => {
    if (error) {
      clearError();
    }
    if (successMessage) {
      setSuccessMessage('');
    }
  }, [formData, clearError]);

  // Email validation
  const validateEmail = (email: string): string | undefined => {
    if (!email) {
      return 'Email is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }

    return undefined;
  };

  // Password validation
  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return 'Password is required';
    }

    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }

    // Check for mixed case, numbers, and special characters
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
      password,
    );

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      return 'Password must contain uppercase, lowercase, numbers, and special characters';
    }

    return undefined;
  };

  // Current password validation (only required when changing password)
  const validateCurrentPassword = (password: string): string | undefined => {
    if (isChangingPassword && !password) {
      return 'Current password is required to change password';
    }
    return undefined;
  };

  // Confirm password validation
  const validateConfirmPassword = (
    confirmPassword: string,
    newPassword: string,
  ): string | undefined => {
    if (isChangingPassword) {
      if (!confirmPassword) {
        return 'Please confirm your new password';
      }

      if (confirmPassword !== newPassword) {
        return 'Passwords do not match';
      }
    }

    return undefined;
  };

  // Real-time validation
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'email':
        return validateEmail(value);
      case 'currentPassword':
        return validateCurrentPassword(value);
      case 'newPassword':
        return isChangingPassword ? validatePassword(value) : undefined;
      case 'confirmPassword':
        return validateConfirmPassword(value, formData.newPassword);
      default:
        return undefined;
    }
  };

  // Handle input change with real-time validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear messages when user starts typing
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }
    if (successMessage) {
      setSuccessMessage('');
    }

    // Real-time validation for touched fields
    if (touched[name]) {
      const fieldError = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: fieldError,
      }));
    }

    // Special handling for confirm password when new password changes
    if (name === 'newPassword' && touched.confirmPassword) {
      const confirmError = validateConfirmPassword(
        formData.confirmPassword,
        value,
      );
      setErrors((prev) => ({
        ...prev,
        confirmPassword: confirmError,
      }));
    }
  };

  // Handle field blur (mark as touched and validate)
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setTouched((prev) => ({ ...prev, [name]: true }));

    const fieldError = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError,
    }));
  };

  // Validate entire form
  const validateForm = (): boolean => {
    const emailError = validateEmail(formData.email);
    const currentPasswordError = validateCurrentPassword(
      formData.currentPassword,
    );
    const newPasswordError = isChangingPassword
      ? validatePassword(formData.newPassword)
      : undefined;
    const confirmPasswordError = validateConfirmPassword(
      formData.confirmPassword,
      formData.newPassword,
    );

    const newErrors: FormErrors = {
      email: emailError,
      currentPassword: currentPasswordError,
      newPassword: newPasswordError,
      confirmPassword: confirmPasswordError,
    };

    setErrors(newErrors);

    // Mark relevant fields as touched
    const touchedFields: Record<string, boolean> = { email: true };
    if (isChangingPassword) {
      touchedFields.currentPassword = true;
      touchedFields.newPassword = true;
      touchedFields.confirmPassword = true;
    }
    setTouched(touchedFields);

    return (
      !emailError &&
      !currentPasswordError &&
      !newPasswordError &&
      !confirmPasswordError
    );
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const updateData: UpdateProfileData = {
        email: formData.email,
      };

      // Add password change data if changing password
      if (isChangingPassword && formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      await updateProfile(updateData);

      // Reset form state on success
      setIsEditing(false);
      setIsChangingPassword(false);
      setFormData((prev) => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
      setTouched({});
      setErrors({});

      setSuccessMessage(
        isChangingPassword
          ? 'Profile and password updated successfully!'
          : 'Profile updated successfully!',
      );
    } catch (err) {
      // Error is handled by the auth context
      console.error('Profile update failed:', err);
    }
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form
      setFormData({
        email: user?.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setErrors({});
      setTouched({});
      setIsChangingPassword(false);
    }
    setIsEditing(!isEditing);
  };

  // Handle password change toggle
  const handlePasswordChangeToggle = () => {
    setIsChangingPassword(!isChangingPassword);
    if (!isChangingPassword) {
      // Clear password fields when enabling password change
      setFormData((prev) => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
      setErrors((prev) => ({
        ...prev,
        currentPassword: undefined,
        newPassword: undefined,
        confirmPassword: undefined,
      }));
    }
  };

  // Check if form has errors
  const hasErrors = Object.values(errors).some((error) => error !== undefined);
  const hasChanges =
    formData.email !== user?.email ||
    (isChangingPassword && formData.newPassword);

  return (
    <div className="profile-page">
      <div className="profile-container">
        <header className="profile-header">
          <h2>Profile Settings</h2>
          <p>Manage your account information and security settings</p>
        </header>

        {/* Success message */}
        {successMessage && (
          <div className="success-message" role="alert">
            {successMessage}
          </div>
        )}

        {/* Error message */}
        {(error || errors.general) && (
          <div className="error-message" role="alert">
            {error || errors.general}
          </div>
        )}

        <div className="profile-content">
          <div className="profile-grid">
            {/* Left Column: User Profile Overview Card */}
            <div className="profile-sidebar">
              <div className="avatar-card">
                <div className="avatar-circle">
                  {user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
                </div>
                <div className="avatar-details">
                  <h3 className="user-email">{user?.email}</h3>
                  <div className="role-container">
                    <span className={`role-badge role-${user?.role}`}>
                      {user?.role?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="stats-card">
                <h3 className="stats-header">Portfolio-Statistik</h3>
                <div className="stats-body">
                  <div className="stats-item">
                    <span className="stats-label">Immobilien im Portfolio</span>
                    <span className="stats-value">
                      {propertyCount !== null ? propertyCount : '...'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Profile details form */}
            <div className="profile-main">
              {/* Profile Information Section */}
              <section className="profile-section">
                <div className="section-header">
                  <h2>Profile Information</h2>
                  {!isEditing && (
                    <button
                      type="button"
                      className="edit-button"
                      onClick={handleEditToggle}
                      disabled={isLoading}
                    >
                      Edit Profile
                    </button>
                  )}
                </div>

                {!isEditing ? (
                  // Display mode
                  <div className="profile-display">
                    <div className="profile-field">
                      <label className="field-label">Email Address</label>
                      <div className="field-value">{user?.email}</div>
                    </div>

                    <div className="profile-field">
                      <label className="field-label">Role</label>
                      <div className="field-value">
                        <span className={`role-badge role-${user?.role}`}>
                          {user?.role?.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <div className="profile-field">
                      <label className="field-label">User ID</label>
                      <div className="field-value">{user?.id}</div>
                    </div>
                  </div>
                ) : (
                  // Edit mode
                  <form onSubmit={handleSubmit} noValidate>
                    {/* Email field */}
                    <div className="form-group">
                      <label htmlFor="email" className="form-label">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.email ? 'error' : ''}`}
                        placeholder="Enter your email"
                        disabled={isLoading}
                        autoComplete="email"
                        aria-describedby={errors.email ? 'email-error' : undefined}
                        aria-invalid={!!errors.email}
                      />
                      {errors.email && (
                        <span id="email-error" className="field-error" role="alert">
                          {errors.email}
                        </span>
                      )}
                    </div>

                    {/* Password change section */}
                    <div className="password-section">
                      <div className="section-header">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={isChangingPassword}
                            onChange={handlePasswordChangeToggle}
                            disabled={isLoading}
                          />
                          <span className="checkbox-text">Change Password</span>
                        </label>
                      </div>

                      {isChangingPassword && (
                        <div className="password-fields">
                          {/* Current password */}
                          <div className="form-group">
                            <label htmlFor="currentPassword" className="form-label">
                              Current Password
                            </label>
                            <input
                              type="password"
                              id="currentPassword"
                              name="currentPassword"
                              value={formData.currentPassword}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              className={`form-input ${errors.currentPassword ? 'error' : ''}`}
                              placeholder="Enter your current password"
                              disabled={isLoading}
                              autoComplete="current-password"
                              aria-describedby={
                                errors.currentPassword
                                  ? 'current-password-error'
                                  : undefined
                              }
                              aria-invalid={!!errors.currentPassword}
                            />
                            {errors.currentPassword && (
                              <span
                                id="current-password-error"
                                className="field-error"
                                role="alert"
                              >
                                {errors.currentPassword}
                              </span>
                            )}
                          </div>

                          {/* New password */}
                          <div className="form-group">
                            <label htmlFor="newPassword" className="form-label">
                              New Password
                            </label>
                            <input
                              type="password"
                              id="newPassword"
                              name="newPassword"
                              value={formData.newPassword}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              className={`form-input ${errors.newPassword ? 'error' : ''}`}
                              placeholder="Enter your new password"
                              disabled={isLoading}
                              autoComplete="new-password"
                              aria-describedby={
                                errors.newPassword
                                  ? 'new-password-error'
                                  : undefined
                              }
                              aria-invalid={!!errors.newPassword}
                            />
                            {errors.newPassword && (
                              <span
                                id="new-password-error"
                                className="field-error"
                                role="alert"
                              >
                                {errors.newPassword}
                              </span>
                            )}
                          </div>

                          {/* Confirm password */}
                          <div className="form-group">
                            <label htmlFor="confirmPassword" className="form-label">
                              Confirm New Password
                            </label>
                            <input
                              type="password"
                              id="confirmPassword"
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                              placeholder="Confirm your new password"
                              disabled={isLoading}
                              autoComplete="new-password"
                              aria-describedby={
                                errors.confirmPassword
                                  ? 'confirm-password-error'
                                  : undefined
                              }
                              aria-invalid={!!errors.confirmPassword}
                            />
                            {errors.confirmPassword && (
                              <span
                                id="confirm-password-error"
                                className="field-error"
                                role="alert"
                              >
                                {errors.confirmPassword}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Form actions */}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={handleEditToggle}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>

                      <button
                        type="submit"
                        className={`submit-button ${isLoading ? 'loading' : ''}`}
                        disabled={isLoading || !hasChanges || hasErrors}
                      >
                        {isLoading ? (
                          <>
                            <span
                              className="loading-spinner"
                              aria-hidden="true"
                            ></span>
                            Updating...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </section>

              {/* Account Management Section */}
              <section className="profile-section">
                <div className="section-header">
                  <h2>Account Management</h2>
                </div>

                <div className="account-actions">
                  <div className="action-item">
                    <div className="action-info">
                      <h3>Account Deactivation</h3>
                      <p>
                        Temporarily deactivate your account. You can reactivate it
                        anytime by logging in.
                      </p>
                    </div>
                    <button
                      className="action-button danger"
                      disabled={true}
                      title="Coming soon"
                    >
                      Deactivate Account
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
